import React from 'react';
import {observer} from 'mobx-react';
import {EPOverlay} from './ep-overlay';
import {UVOverlay} from './uv-overlay';
import {ProductCustomizationState} from './render.def';
// IMPORTANT: MUST NOT import from s.ts (product store) - this renderer code is used in multiple apps for image rendering

declare var __image_gen: boolean;
declare var __product_page: boolean;

interface Props {
    pcs: ProductCustomizationState;
}

const getOldStyleCompositeSrc = (pcs: ProductCustomizationState): string => {
    const composite = (pcs.old_style_composite || '').toLowerCase();
    if (!composite) return '';
    return composite.includes('oldstyle') ? `https://${pcs.CDNDomain}/images/products/composites/${composite}` : `https://${pcs.CDNDomain}/images/products/composites/primary/${composite}`;
};

// Renders CustomizationMethodID = 1
export const FrameOld = observer(({pcs}: Props) => {
    // When old_true, moulding wraps around the outside of the image (no overlap)
    const outerMoulding = pcs.old_true;
    const mouldingOffset = outerMoulding ? pcs.css.moulding_width : pcs.css.moulding_half;
    const compositeSrc = getOldStyleCompositeSrc(pcs);

    return (
        <div
            id="frame-old-container"
            style={{padding: outerMoulding ? `${pcs.css.moulding_width}px` : `${pcs.css.moulding_half}px 0`}}
            role="img"
            aria-label={pcs.ADA}
        >
            {/* isolation keeps the stack's z-indexes (moulding z4, EP/UV overlays z5)
                local: without it they join the page's root stacking context, tie the
                header's open nav menus (.nav-expanded, also z5) and, being later in
                the DOM, paint through the open menu. */}
            <div
                id="frame-old-container-inner"
                style={{isolation: 'isolate'}}
            >
                <img
                    id="frame-old"
                    className="img-core"
                    src={compositeSrc}
                    alt={`Product Image of ${pcs.ProductName_Original}`}
                    onError={e => {
                        const img = e.currentTarget;
                        const fallback = pcs.Image_Full ? `https://${pcs.CDNDomain}/images/products/full/${pcs.Image_Full.toLowerCase()}` : '';
                        if (fallback && img.src !== fallback) {
                            img.src = fallback;
                        }
                    }}
                />
                <img
                    id="m_topleft"
                    className="old-moulding z4"
                    alt=""
                    src={pcs.css.m_topleft_src}
                    style={{
                        left: `-${mouldingOffset}px`,
                        top: `-${mouldingOffset}px`,
                        width: pcs.css.m_topleft_width,
                    }}
                />
                <img
                    id="m_topright"
                    className="old-moulding z4"
                    alt=""
                    src={pcs.css.m_topright_src}
                    style={{
                        right: `-${mouldingOffset}px`,
                        top: `-${mouldingOffset}px`,
                        width: pcs.css.m_topright_width,
                    }}
                />
                <img
                    id="m_bottomright"
                    className="old-moulding z4"
                    alt=""
                    src={pcs.css.m_bottomright_src}
                    style={{
                        right: `-${mouldingOffset}px`,
                        bottom: `-${mouldingOffset}px`,
                        width: pcs.css.m_bottomright_width,
                    }}
                />
                <img
                    id="m_bottomleft"
                    className="old-moulding z4"
                    alt=""
                    src={pcs.css.m_bottomleft_src}
                    style={{
                        left: `-${mouldingOffset}px`,
                        bottom: `-${mouldingOffset}px`,
                        width: pcs.css.m_bottomleft_width,
                    }}
                />
                <img
                    id="m_left"
                    className="old-moulding z3"
                    alt=""
                    src={pcs.css.m_left_src}
                    style={{
                        left: `-${mouldingOffset}px`,
                        top: 0,
                        width: pcs.css.m_left_width,
                        height: pcs.css.m_left_height,
                        objectFit: 'fill',
                    }}
                />
                <img
                    id="m_top"
                    className="old-moulding z3 w100"
                    alt=""
                    src={pcs.css.m_top_src}
                    style={{
                        top: `-${mouldingOffset}px`,
                        left: 0,
                        width: pcs.css.m_top_width,
                        height: pcs.css.m_top_height,
                    }}
                />
                <img
                    id="m_right"
                    className="old-moulding z3"
                    alt=""
                    src={pcs.css.m_right_src}
                    style={{
                        right: `-${mouldingOffset}px`,
                        top: 0,
                        width: pcs.css.m_right_width,
                        height: pcs.css.m_right_height,
                        objectFit: 'fill',
                    }}
                />
                <img
                    id="m_bottom"
                    className="old-moulding z3 w100"
                    alt=""
                    src={pcs.css.m_bottom_src}
                    style={{
                        bottom: `-${mouldingOffset}px`,
                        left: 0,
                        width: pcs.css.m_bottom_width,
                        height: pcs.css.m_bottom_height,
                    }}
                />
                {pcs.ProductTypeID === 5 && !__image_gen && __product_page && (
                    <div className="tassel-cord tassel-cord-1">
                        <picture>
                            <source
                                type="image/webp"
                                srcSet={`https://${pcs.CDNDomain}/images/cached/general/honor-cord1.webp`}
                            />
                            <img
                                width="128"
                                height="128"
                                src={`https://${pcs.CDNDomain}/images/cached/general/honor-cord1.png`}
                                alt="Frame Also Holds Cord"
                                loading="lazy"
                            />
                        </picture>
                    </div>
                )}

                {/* Last children so the plate/UV text paint over the moulding imgs
                    (zIndex 5 > z4). No __image_gen guard - the overlays must burn
                    into headless renders. The second EPOverlay is the template's
                    optional second plate (#ep-oldstyle-overlay-2). */}
                <EPOverlay pcs={pcs} />
                <EPOverlay
                    pcs={pcs}
                    plate={2}
                />
                <UVOverlay pcs={pcs} />
            </div>
        </div>
    );
});
